import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { InputV2 } from '../InputV2';
import { Button } from '../Button';
import { EmojiIcon } from '../EmojiIcon';
import CustomLink from '../CustomLink';
import { PRIVACY_POLICY_LINK, SUPPORT_EMAIL } from '../../shared/links';
import { useSendResults } from '../../shared/use-send-results';
import styles from './EmailSlideV3.css';

function EmailSlideV3({
    setEmail,
    onContinue,
    results,
    spriteVersion,
    legalEntity,
    legalLinks = { privacyPolicy: PRIVACY_POLICY_LINK }
}) {
    const emailRef = useRef(null);
    const afterSuccessfulSendResults = callback => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });

        callback(); /* eslint-disable-line */
        onContinue();
    };

    const { fields, changeField, isPending, submitForm, errors } = useSendResults({ saveEmail: setEmail, afterCallback: afterSuccessfulSendResults, initialPrelander: results?.version });
    const isDidYouMean = Boolean(errors.form?.didYouMean);

    useEffect(() => {
        setTimeout(() => {
            emailRef.current?.focus();
        }, 50);
    }, []);

    return (
        <form
            className={styles.form}
            onSubmit={submitForm}
        >
            <div className={styles.content}>
                <div className={styles.head}>
                    <h1 className={styles.title}>
                        Your Digital Memoir is ready
                    </h1>
                    <p className={styles.description}>Enter your email to save your plan and see what’s inside.</p>
                </div>
                <InputV2
                    value={fields.email}
                    name='email'
                    onChange={changeField}
                    className={styles.input}
                    placeholder='Email address'
                    errorText={errors.email || (!isDidYouMean && errors.form?.message)}
                    disabled={isPending}
                    inputProps={{ autoComplete: 'email', type: 'email', inputMode: 'email' }}
                    emailDomainSuggestions={{
                        list: ['gmail.com', 'yahoo.com', 'icloud.com', 'hotmail.com', 'aol.com', 'outlook.com', 'comcast.net', 'att.net', 'live.com', 'mail.com', 'msn.com']
                    }}
                    inputRef={emailRef}
                    didYouMean={errors.form?.didYouMean}
                />
                <div className={styles.coupon}>
                    <EmojiIcon name='party-popper' version={spriteVersion} className={styles.couponEmoji} />
                    <p className={styles.couponText}>
                        {'You’re eligible to redeem the coupon code '}
                        <strong>STORY2026</strong>
                        {' in the next steps and '}
                        <strong>save up to 42%</strong>
                        {' on your order.'}
                    </p>
                </div>
                <p className={styles.agreementText}>
                    {'We respect your privacy and are committed to protecting your personal data. Your data will be processed in accordance with our '}
                    <CustomLink
                        to={legalLinks.privacyPolicy}
                        target='_blank'
                    >
                        Privacy&nbsp;Policy
                    </CustomLink>
                    .
                </p>
                <Button
                    type='ink'
                    label='Continue'
                    iconRight={{ name: 'chevronRight' }}
                    isLoading={isPending}
                    inSlide
                    buttonProps={{ type: 'submit' }}
                />
            </div>
            <p className={styles.copyright}>
                {`©${new Date().getFullYear()} Boundly. All rights reserved.`}
                <br />
                {`${legalEntity.company} · ${legalEntity.address} · `}
                <CustomLink to={`mailto:${SUPPORT_EMAIL}`} target='_blank'>{SUPPORT_EMAIL}</CustomLink>
            </p>
        </form>
    );
}

EmailSlideV3.propTypes = {
    setEmail: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
    results: PropTypes.shape({ version: PropTypes.string }),
    spriteVersion: PropTypes.string.isRequired,
    legalEntity: PropTypes.shape({
        company: PropTypes.string,
        address: PropTypes.string
    }).isRequired,
    legalLinks: PropTypes.object
};

export {
    EmailSlideV3
};
